/* V160 — Admin Roadmap Tab (Kanban board).
   4 columns: BACKLOG · NEXT · IN PROGRESS · SHIPPED
   Includes ALL services we provide:
   - 34 AI agents (live + coming-soon)
   - 10+ integrations (Stripe, Resend, SF, FB CAPI, Claude, OpenAI, Gemini, OAuth)
   - User-facing features (scan, Blog Agent, Revenue Leak, Ad Studio batch, etc.)

   Drag-and-drop status (via HTML5 DnD) → persists to /api/roadmap/update-status
   When an agent moves to SHIPPED, all waitlist voters get a "🎉 LIVE" email.
*/
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";const STATUS_META = {
  backlog:     { label: "📋 Backlog",     color: "border-zinc-600",    bg: "bg-zinc-950" },
  next:        { label: "⏭️ Next up",     color: "border-blue-500",    bg: "bg-blue-950/30" },
  in_progress: { label: "⚙️ In progress", color: "border-amber-500",   bg: "bg-amber-950/30" },
  shipped:     { label: "✅ Shipped",     color: "border-emerald-500", bg: "bg-emerald-950/30" },
};

const CATEGORY_COLORS = {
  agent: "text-purple-300 bg-purple-900/40",
  service: "text-blue-300 bg-blue-900/40",
  feature: "text-amber-300 bg-amber-900/40",
  product: "text-pink-300 bg-pink-900/40",
};

export default function AdminRoadmapTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all"); // all | agent | service | feature | product
  const [dragId, setDragId] = useState(null);
  const [moveToast, setMoveToast] = useState("");

  const adminToken = (typeof window !== "undefined" && localStorage.getItem("admin_token")) || "";
  const headers = { "X-Admin-Token": adminToken };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/roadmap/items`, { headers });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.status === 401
        ? "Admin token chýba alebo je neplatný. Skontroluj localStorage.admin_token"
        : "Roadmap dáta nedostupné.");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    const handler = () => fetchData();
    window.addEventListener("roadmap-refresh", handler);
    return () => window.removeEventListener("roadmap-refresh", handler);
  }, [fetchData]);

  const move = async (id, newStatus) => {
    try {
      const r = await axios.post(`${API}/roadmap/update-status`, { id, status: newStatus }, { headers });
      const notified = r.data?.notified_count || 0;
      setMoveToast(
        notified > 0
          ? `✓ Status updatnutý · 📧 Odoslaných ${notified} notifikácií waitlistu`
          : `✓ Status updatnutý`
      );
      setTimeout(() => setMoveToast(""), 4000);
      await fetchData();
    } catch (e) {
      setMoveToast("✗ Update zlyhal");
    }
  };

  const filteredGrouped = (() => {
    if (!data?.grouped) return null;
    if (filter === "all") return data.grouped;
    const out = { backlog: [], next: [], in_progress: [], shipped: [] };
    Object.entries(data.grouped).forEach(([s, items]) => {
      out[s] = items.filter((i) => i.category === filter);
    });
    return out;
  })();

  return (
    <div className="p-4 space-y-4" data-testid="admin-roadmap-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          🛠️ Build Roadmap · Všetky služby
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="text-xs bg-amber-500 hover:bg-amber-400 text-black font-bold px-3 py-1"
            data-testid="roadmap-refresh"
          >
            ↻ Obnoviť
          </button>
        </div>
      </div>

      {err && <div className="bg-red-900/40 border border-red-700 text-red-200 text-xs p-3">{err}</div>}
      {moveToast && (
        <div className="bg-emerald-900/40 border border-emerald-700 text-emerald-200 text-xs p-3" data-testid="roadmap-toast">
          {moveToast}
        </div>
      )}

      {/* V160.1 — Potential MRR Forecast banner */}
      {data?.potential_mrr_unshipped > 0 && (
        <div className="bg-gradient-to-r from-emerald-950/60 to-zinc-950 border border-emerald-500/40 p-4" data-testid="roadmap-mrr-banner">
          <div className="text-[10px] tracking-widest uppercase font-black text-emerald-400 mb-1">
            💰 Potenciálne MRR · Ak shippneš všetko mimo SHIPPED
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-black text-white" data-testid="roadmap-mrr-total">
              + € {(data.potential_mrr_unshipped || 0).toLocaleString("sk-SK")}
            </div>
            <div className="text-xs text-zinc-500">/mes</div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">
            Vzorec: demand_votes × cena/mes per agent · poradie v stĺpcoch je sortované podľa MRR desc (najvyššie hore)
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-1 flex-wrap">
        {[
          { id: "all", label: `Všetko (${data?.items?.length || 0})` },
          { id: "agent", label: "🤖 AI Agenti" },
          { id: "service", label: "🔌 Integrácie/Services" },
          { id: "feature", label: "✨ Features" },
          { id: "product", label: "📦 Produkty" },
        ].map((c) => (
          <button
            key={c.id}
            onClick={() => setFilter(c.id)}
            className={`text-xs px-3 py-1 ${
              filter === c.id ? "bg-amber-500 text-black font-bold" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
            }`}
            data-testid={`roadmap-filter-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-xs text-zinc-500">Načítavam…</p>}

      {filteredGrouped && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {Object.entries(STATUS_META).map(([statusKey, meta]) => (
            <div
              key={statusKey}
              className={`${meta.bg} border-t-4 ${meta.color} border-x border-b border-zinc-800 min-h-[400px]`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId) { move(dragId, statusKey); setDragId(null); }
              }}
              data-testid={`roadmap-col-${statusKey}`}
            >
              <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="font-black text-white text-xs uppercase tracking-wider">{meta.label}</h3>
                <span className="text-[10px] bg-zinc-900 text-zinc-400 px-2 py-0.5">
                  {filteredGrouped[statusKey].length}
                </span>
              </div>
              <div className="p-2 space-y-2 max-h-[600px] overflow-y-auto">
                {filteredGrouped[statusKey].length === 0 && (
                  <p className="text-[10px] text-zinc-600 italic text-center py-4">— prázdne —</p>
                )}
                {filteredGrouped[statusKey].map((it) => (
                  <RoadmapCard
                    key={it.id}
                    item={it}
                    onDragStart={() => setDragId(it.id)}
                    onMove={(newStatus) => move(it.id, newStatus)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-zinc-600 pt-2 border-t border-zinc-800">
        💡 Tip: Drag-and-drop karty medzi stĺpcami pre zmenu statusu. Keď agent ide do <span className="text-emerald-400">✅ Shipped</span>, všetci čo hlasovali na waitliste dostanú automaticky email "🎉 LIVE".
      </p>
    </div>
  );
}

function RoadmapCard({ item, onDragStart, onMove }) {
  const catColor = CATEGORY_COLORS[item.category] || "text-zinc-300 bg-zinc-800";
  const [showLaunchInput, setShowLaunchInput] = useState(false);
  const [launchVal, setLaunchVal] = useState(item.launch_at ? item.launch_at.slice(0, 16) : "");

  const saveLaunch = async () => {
    const token = (typeof window !== "undefined" && localStorage.getItem("admin_token")) || "";
    const headers = { "X-Admin-Token": token };
    try {
      // Convert datetime-local to ISO with UTC
      const iso = launchVal ? new Date(launchVal).toISOString() : null;
      await axios.post(`${API}/roadmap/set-launch-date`, { id: item.id, launch_at: iso }, { headers });
      setShowLaunchInput(false);
      window.dispatchEvent(new CustomEvent("roadmap-refresh"));
    } catch (e) { /* noop */ }
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className="bg-zinc-900 border border-zinc-800 hover:border-amber-500 p-2 cursor-grab active:cursor-grabbing transition-colors"
      data-testid={`roadmap-card-${item.id}`}
    >
      <div className="flex items-start gap-2">
        <span className="text-base flex-shrink-0">{item.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap mb-1">
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${catColor}`}>
              {item.category}
            </span>
            {item.demand_votes > 0 && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold bg-amber-900 text-amber-300">
                {item.demand_votes} 🔥
              </span>
            )}
            {item.potential_mrr_eur > 0 && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold bg-emerald-900 text-emerald-300" title="Potenciálne MRR ak shipnuté: demand_votes × cena">
                +€{item.potential_mrr_eur}/mo
              </span>
            )}
            {item.notified_count > 0 && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold bg-emerald-900 text-emerald-300" title="Emaily odoslané">
                📧 {item.notified_count}
              </span>
            )}
            {item.launch_at && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold bg-purple-900 text-purple-300" title={`Launch at ${item.launch_at}`}>
                ⏰ {item.launch_at.slice(0, 10)}
              </span>
            )}
          </div>
          <div className="font-bold text-white text-xs leading-tight">{item.title}</div>
          {item.desc && <div className="text-[10px] text-zinc-500 mt-0.5 line-clamp-2">{item.desc}</div>}
        </div>
      </div>
      <div className="flex gap-1 mt-2 flex-wrap">
        {Object.keys(STATUS_META)
          .filter((s) => s !== item.status)
          .map((s) => (
            <button
              key={s}
              onClick={() => onMove(s)}
              className="text-[9px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-1.5 py-0.5"
              title={`Presunúť do ${STATUS_META[s].label}`}
            >
              → {s.slice(0, 4)}
            </button>
          ))}
        {item.agent_slug && (
          <button
            onClick={() => setShowLaunchInput((v) => !v)}
            className="text-[9px] bg-purple-900 hover:bg-purple-800 text-purple-200 px-1.5 py-0.5"
            title="Nastaviť launch dátum (countdown sa zobrazí na /agent/<slug>)"
            data-testid={`roadmap-launch-toggle-${item.id}`}
          >
            ⏰ launch
          </button>
        )}
      </div>
      {showLaunchInput && item.agent_slug && (
        <div className="mt-2 p-2 bg-zinc-950 border border-zinc-800 space-y-1">
          <input
            type="datetime-local"
            value={launchVal}
            onChange={(e) => setLaunchVal(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 text-xs text-white px-2 py-1"
            data-testid={`roadmap-launch-input-${item.id}`}
          />
          <div className="flex gap-1">
            <button onClick={saveLaunch} className="text-[10px] bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-2 py-1 flex-1">
              ✓ Uložiť
            </button>
            <button onClick={() => { setLaunchVal(""); saveLaunch(); }} className="text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2 py-1">
              ✗ Zrušiť
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
